import React, { useContext, useState } from "react";
import {
    Box, VStack, FormControl, Input, Button, InputLeftAddon,
    InputGroup, useToast, Text, Icon, Flex, Link
} from "@chakra-ui/react";
import { LaundryContext } from "../Contexts/LaundryContext";
import { FiPhone, FiMail } from "react-icons/fi";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function LoginPage({ onLoginSubmit, isLoginLoading, initialPhoneNumber = "", initialEmail = "" }) {
    const strippedPhoneNumber = initialPhoneNumber.slice(-10);
    const [phoneNumber, setPhoneNumber] = useState(strippedPhoneNumber);
    const [email, setEmail] = useState(initialEmail);
    const [useEmail, setUseEmail] = useState(!!initialEmail && !initialPhoneNumber);
    const { laundryData } = useContext(LaundryContext);
    const toast = useToast();

    const handleLoginSubmit = () => {
        if (useEmail) {
            const trimmed = email.trim().toLowerCase();
            if (!EMAIL_RE.test(trimmed)) {
                toast({
                    title: "Invalid email",
                    description: "Please enter a valid email address.",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
            onLoginSubmit({ email: trimmed });
            return;
        }
        const digitsOnly = phoneNumber.replace(/\D/g, '');
        if (digitsOnly.length !== 10) {
            toast({
                title: "Invalid phone number",
                description: "Please enter a 10-digit US phone number.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        onLoginSubmit({ phone: `+1${digitsOnly}` });
    };

    const inputStyle = {
        bg: "white",
        border: "1px solid",
        borderColor: "gray.200",
        _focus: { borderColor: "blue.400", boxShadow: "0 0 0 1px #63b3ed" },
        fontSize: "lg",
        onKeyPress: (e) => e.key === 'Enter' && handleLoginSubmit(),
    };

    return (
        <Box w="100%" maxW="400px" mx="auto" px={6} py={8}>
            <VStack spacing={6} align="stretch">
                {/* Logo / Brand */}
                <VStack spacing={2} mb={4}>
                    <Flex
                        w="64px" h="64px" borderRadius="full"
                        bg="blue.50" align="center" justify="center"
                    >
                        <Icon as={useEmail ? FiMail : FiPhone} boxSize={6} color="blue.500" />
                    </Flex>
                    <Text fontSize="2xl" fontWeight="700" color="gray.800" textAlign="center">
                        {laundryData?.laundryName || "Smart Laundry"}
                    </Text>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                        {useEmail ? "Enter your email to get started" : "Enter your phone number to get started"}
                    </Text>
                </VStack>

                <FormControl>
                    {useEmail ? (
                        <Input
                            size="lg"
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            data-testid="login-email"
                            {...inputStyle}
                        />
                    ) : (
                        <InputGroup size="lg">
                            <InputLeftAddon
                                bg="gray.50" border="1px solid" borderColor="gray.200"
                                color="gray.600" fontWeight="500"
                            >
                                +1
                            </InputLeftAddon>
                            <Input
                                type="tel"
                                placeholder="(512) 555-0123"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                {...inputStyle}
                            />
                        </InputGroup>
                    )}
                </FormControl>

                <Button
                    isLoading={isLoginLoading}
                    loadingText="Verifying..."
                    bg="linear-gradient(135deg, #4299E1 0%, #63B3ED 100%)"
                    color="white"
                    size="lg"
                    w="full"
                    borderRadius="xl"
                    fontWeight="600"
                    _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
                    _active={{ transform: "translateY(0)" }}
                    transition="all 0.2s"
                    onClick={handleLoginSubmit}
                >
                    Continue
                </Button>

                <Button
                    variant="link"
                    color="blue.500"
                    fontSize="sm"
                    fontWeight="500"
                    onClick={() => setUseEmail((v) => !v)}
                    data-testid="login-mode-toggle"
                >
                    {useEmail ? "Use my phone number instead" : "Prefer email? Sign in with email instead"}
                </Button>

                {useEmail ? (
                    <Text fontSize="xs" color="gray.500" textAlign="center" px={2}>
                        We'll email you a one-time sign-in code. No text messages.{" "}
                        <Link href="/privacy-policy" isExternal color="blue.500">Privacy</Link>
                    </Text>
                ) : (
                    <Text fontSize="xs" color="gray.500" textAlign="center" px={2}>
                        By tapping Continue, you agree to receive a one-time verification code by text from
                        Smart Laundry Basket. Msg &amp; data rates may apply. Reply HELP for help, STOP to opt out.{" "}
                        <Link href="/terms#sms" isExternal color="blue.500">SMS Terms</Link> ·{" "}
                        <Link href="/privacy-policy" isExternal color="blue.500">Privacy</Link>
                    </Text>
                )}
            </VStack>
        </Box>
    );
}
